import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, ShieldAlert, CheckCircle2, RotateCcw } from 'lucide-react';

import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';

export const CheckoutReviewsTab = ({ patientId }) => {
  const [reviews, setReviews] = useState(null);
  const [noteDraft, setNoteDraft] = useState({});
  const [busy, setBusy] = useState({});

  const load = async () => {
    try { const r = await api.get(`/care/customers/${patientId}/checkout-reviews`); setReviews(r.data.reviews); }
    catch { toast.error('Could not load checkout reviews'); }
  };
  useEffect(() => { load(); }, [patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const addNote = async (overrideId) => {
    const body = (noteDraft[overrideId] || '').trim();
    if (!body) return;
    setBusy((b) => ({ ...b, [overrideId]: true }));
    try {
      await api.post(`/care/customers/${patientId}/checkout-reviews/${overrideId}/notes`, { body });
      setNoteDraft((d) => ({ ...d, [overrideId]: '' }));
      await load();
    } catch (e) { toast.error('Could not add note'); }
    finally { setBusy((b) => ({ ...b, [overrideId]: false })); }
  };

  const toggleStatus = async (overrideId, current) => {
    const next = current === 'reviewed' ? 'open' : 'reviewed';
    try { await api.put(`/care/customers/${patientId}/checkout-reviews/${overrideId}/status`, { status: next }); await load(); }
    catch (e) { toast.error('Could not update status'); }
  };

  if (!reviews) return <p className="text-sm text-muted-foreground flex items-center gap-1" data-testid="care-reviews-loading"><Loader2 className="w-4 h-4 animate-spin" />Loading checkout reviews…</p>;
  if (!reviews.length) return <p className="care-empty" data-testid="care-reviews-empty">No clinical-safety overrides recorded for this customer yet.</p>;

  return <div className="space-y-3" data-testid="care-reviews-list">
    {reviews.map((r) => <article key={r.override_id} className="care-record space-y-2" data-testid={`care-review-${r.override_id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-amber-600" /><span className="text-sm font-medium">{new Date(r.created_at).toLocaleString()}</span></div>
        <button type="button" onClick={() => toggleStatus(r.override_id, r.status)} aria-label={r.status === 'reviewed' ? 'Mark as open' : 'Mark as reviewed'} className={`text-xs px-2 py-0.5 rounded-full font-medium cursor-pointer transition-opacity hover:opacity-75 ${r.status === 'reviewed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-testid={`care-review-status-${r.override_id}`}>
          {r.status === 'reviewed' ? <><CheckCircle2 className="w-3 h-3 inline mr-1" />Reviewed</> : <><RotateCcw className="w-3 h-3 inline mr-1" />Open</>}
        </button>
      </div>
      <p className="text-xs text-muted-foreground">Cashier: <b>{r.cashier_name || '—'}</b> · Authorised by: <b>{r.pharmacist_name || '—'}</b></p>
      {r.matched_allergies?.length > 0 && <p className="text-xs text-red-600" data-testid={`care-review-matches-${r.override_id}`}>Matched allergies: {r.matched_allergies.join(', ')}</p>}
      {r.note && <p className="text-sm whitespace-pre-wrap" data-testid={`care-review-note-${r.override_id}`}>{r.note}</p>}
      {(r.follow_up_notes || []).length > 0 && <div className="pl-3 border-l-2 border-muted space-y-1">
        {r.follow_up_notes.map((n) => <div key={n.note_id} data-testid={`care-review-followup-${n.note_id}`}>
          <p className="text-sm whitespace-pre-wrap">{n.body}</p>
          <p className="text-xs text-muted-foreground">{n.author_name || '—'} · {new Date(n.created_at).toLocaleString()}</p>
        </div>)}
      </div>}
      <div className="flex gap-2 items-start">
        <Textarea rows={2} value={noteDraft[r.override_id] || ''} onChange={(e) => setNoteDraft((d) => ({ ...d, [r.override_id]: e.target.value }))} placeholder="Add a follow-up note…" data-testid={`care-review-note-input-${r.override_id}`} />
        <Button size="sm" onClick={() => addNote(r.override_id)} disabled={busy[r.override_id] || !(noteDraft[r.override_id] || '').trim()} data-testid={`care-review-note-add-${r.override_id}`}>Add</Button>
      </div>
    </article>)}
  </div>;
};
